import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useQueryClient } from "@tanstack/react-query";
import { Wifi, WifiOff, RefreshCw, Smartphone, LogOut } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useDisconnectWhatsApp, useWhatsAppStatus } from "@/hooks/useWhatsApp";
import { WhatsAppBlacklist } from "@/components/WhatsAppBlacklist";
import { WhatsAppNotificationPhones } from "@/components/WhatsAppNotificationPhones";
import { WhatsAppHandoffOnlyNotifications } from "@/components/WhatsAppHandoffOnlyNotifications";
import { WhatsAppWhitelist } from "@/components/WhatsAppWhitelist";
import { WabaEmbeddedSignup } from "@/components/WabaEmbeddedSignup";
import { WHATSAPP_TRANSPORT_LABELS } from "@school-sales-ai/shared";

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function WhatsAppConnect(): React.JSX.Element {
  const { data: status, isPending } = useWhatsAppStatus();
  const disconnect = useDisconnectWhatsApp();
  const queryClient = useQueryClient();

  const [qr, setQr] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // The QR SSE stream is Baileys-only; other transports use Meta's Embedded Signup popup instead.
    if (isPending || status?.connected || status?.transport !== "baileys") return;

    const controller = new AbortController();
    abortRef.current = controller;
    setQr(null);
    setTimedOut(false);
    setStreamError(null);

    async function startStream(): Promise<void> {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      // Mirror the api helper: scope the request to the active client (super-admins send X-Client-Id;
      // regular admins are scoped server-side). Without it, requireClientScope rejects super-admins.
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const activeClientId = useClientSwitcherStore.getState().activeClientId;
      if (activeClientId) headers["X-Client-Id"] = activeClientId;

      const response = await fetch(`${BASE_URL}/crm/whatsapp/qr`, {
        headers,
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        setStreamError(`Error ${response.status}`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const part of parts) {
          let eventType = "";
          let data = "";
          for (const line of part.split("\n")) {
            if (line.startsWith("event: ")) eventType = line.slice(7);
            else if (line.startsWith("data: ")) data = line.slice(6);
          }

          if (eventType === "qr") {
            const parsed = JSON.parse(data) as { qr: string };
            setQr(parsed.qr);
          } else if (eventType === "connected") {
            void queryClient.invalidateQueries({
              queryKey: ["whatsapp-status"],
            });
            return;
          } else if (eventType === "error") {
            // The backend refused the pairing for a reason the admin can act on (e.g. the number is
            // already linked to another client). Rendered by the same ErrorCard as a stream failure.
            const parsed = JSON.parse(data) as { message?: string };
            setStreamError(parsed.message ?? "No se pudo vincular el número.");
            return;
          } else if (eventType === "timeout") {
            setTimedOut(true);
            return;
          }
        }
      }
    }

    startStream().catch((err: unknown) => {
      if ((err as { name?: string }).name === "AbortError") return;
      setStreamError(
        err instanceof Error ? err.message : "Error desconocido",
      );
    });

    return () => {
      controller.abort();
    };
  }, [isPending, status?.connected, status?.transport, retryKey, queryClient]);

  function retry(): void {
    setRetryKey((k) => k + 1);
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-ink-100 px-6 py-5">
        <div>
          <h1 className="text-lg font-semibold text-ink-900">
            Conexión WhatsApp
          </h1>
          <p className="mt-0.5 text-sm text-ink-500">
            Vincula la cuenta de WhatsApp del colegio al sistema.
          </p>
        </div>
        {status && (
          <span
            className="rounded-full border border-ink-200 bg-ink-050 px-3 py-1 text-xs font-medium text-ink-600"
            title="El transporte se configura en Clientes (super-admin)"
          >
            {WHATSAPP_TRANSPORT_LABELS[status.transport]}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col items-center gap-6 overflow-y-auto p-6">
        {isPending ? (
          <span className="text-sm text-ink-500">Cargando…</span>
        ) : status?.connected ? (
          <ConnectedCard
            phone={status.phone}
            connectedSince={status.connectedSince}
            onDisconnect={() => disconnect.mutate()}
            isDisconnecting={disconnect.isPending}
          />
        ) : status?.transport === "cloud_api" ||
          status?.transport === "cloud_api_coexistence" ? (
          <WabaEmbeddedSignup transport={status.transport} />
        ) : timedOut || streamError ? (
          <ErrorCard
            message={
              timedOut
                ? "El código QR expiró sin ser escaneado."
                : (streamError ?? "Error al conectar.")
            }
            onRetry={retry}
          />
        ) : (
          <QrCard qr={qr} />
        )}
        <WhatsAppNotificationPhones />
        {status?.transport === "baileys" && <WhatsAppHandoffOnlyNotifications />}
        <WhatsAppWhitelist />
        <WhatsAppBlacklist />
      </div>
    </div>
  );
}

function ConnectedCard({
  phone,
  connectedSince,
  onDisconnect,
  isDisconnecting,
}: {
  phone: string | null;
  connectedSince: string | null;
  onDisconnect: () => void;
  isDisconnecting: boolean;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-5 rounded-xl border border-ink-100 bg-white p-8 shadow-sm">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
        <Wifi className="text-green-600" size={26} />
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-ink-900">Conectado</p>
        {phone && (
          <p className="mt-1 font-mono text-sm text-ink-500">+{phone}</p>
        )}
        {connectedSince && (
          <p className="mt-0.5 text-xs text-ink-400">
            Desde {formatDate(connectedSince)}
          </p>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={isDisconnecting}
        className="gap-2"
      >
        {isDisconnecting ? (
          <span className="flex items-center gap-2">
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-ink-300 border-t-ink-600" />
            Desconectando…
          </span>
        ) : (
          <>
            <LogOut size={14} />
            Desconectar
          </>
        )}
      </Button>

      {/* Destructive action → explicit confirmation modal (Baileys logs out the device; WABA clears
          the stored credentials). Either way reconnecting means re-linking the account. */}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desconectar WhatsApp?</AlertDialogTitle>
            <AlertDialogDescription>
              Se cerrará la conexión de WhatsApp del colegio
              {phone ? ` (+${phone})` : ""}. Para volver a operar tendrás que vincular la cuenta de
              nuevo. Esto no borra las conversaciones existentes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDisconnect}
              disabled={isDisconnecting}
              className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600"
            >
              Sí, desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function QrCard({ qr }: { qr: string | null }): React.JSX.Element {
  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-6 rounded-xl border border-ink-100 bg-white p-8 shadow-sm">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-ink-100">
        <Smartphone className="text-ink-500" size={26} />
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-ink-900">
          Escanea el código QR
        </p>
        <p className="mt-1 text-sm text-ink-500">
          Abre WhatsApp → Dispositivos vinculados → Vincular dispositivo
        </p>
      </div>

      <div className="flex h-[256px] w-[256px] items-center justify-center rounded-lg border border-ink-100 bg-ink-050">
        {qr ? (
          <QRCodeCanvas value={qr} size={240} />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-ink-300 border-t-ink-700" />
            <span className="text-xs text-ink-400">Generando…</span>
          </div>
        )}
      </div>

      <p className="text-center text-xs text-ink-400">
        El código expira en 60 segundos.
      </p>
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-5 rounded-xl border border-ink-100 bg-white p-8 shadow-sm">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
        <WifiOff className="text-red-500" size={26} />
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-ink-900">Sin conexión</p>
        <p className="mt-1 text-sm text-ink-500">{message}</p>
      </div>
      <Button variant="outline" onClick={onRetry} className="gap-2">
        <RefreshCw size={14} />
        Reintentar
      </Button>
    </div>
  );
}
